import { MAX_PINNED_NOTES } from '@/constants'
import { now } from '@/lib/utils'
import { fetchMuteList, publishOpaquely } from '@/services/client.service'
import {
  Emoji,
  fetchEmojisWithSets,
  fetchFavoriteFollowSetsWithSets,
  fetchFavoriteRelaysWithSets,
  loadBlockedRelays,
  loadBlossomServers,
  loadFollowsList,
  loadPins,
  loadRelayList,
  loadSearchRelays,
  loadSimpleGroups
} from '@nostr/gadgets/lists'
import { ResolvedSet } from '@nostr/gadgets/sets'
import * as kinds from '@nostr/tools/kinds'
import { atom, getDefaultStore } from 'jotai'
import {
  muteListChannelAtom,
  searchRelaysChannelAtom,
  blockedRelaysChannelAtom,
  favoritesChannelAtom,
  followListChannelAtom,
  pinListChannelAtom,
  emojiListChannelAtom,
  invalidateFollowList,
  relayListChannelAtom,
  blossomChannelAtom,
  simpleGroupsChannelAtom,
  favoriteFollowSetsChannelAtom
} from '@/atoms/invalidation'
import { currentPubkeyAtom } from './session'
import { unwrap } from 'jotai/utils'
import { toast } from 'sonner'
import i18n from 'i18next'
import { getErrorMessage } from '@/lib/error'

const store = getDefaultStore()
const t = i18n.t.bind(i18n)

export const pinListAtom = atom(async (get) => {
  const pubkey = get(currentPubkeyAtom)
  get(pinListChannelAtom)
  if (!pubkey) return [] as string[]

  const pins = await loadPins(pubkey)
  return pins.items.slice(0, MAX_PINNED_NOTES)
})
export const unwrappedPinListAtom = unwrap(pinListAtom)

export const userEmojiListAtom = atom(async (get) => {
  const pubkey = get(currentPubkeyAtom)
  get(emojiListChannelAtom)
  if (!pubkey) return [] as (Emoji | ResolvedSet<Emoji>)[]

  const items = await fetchEmojisWithSets(pubkey)
  return items
})
export const unwrappedUserEmojiListAtom = unwrap(userEmojiListAtom)

export const muteListAtom = atom(async (get) => {
  const pubkey = get(currentPubkeyAtom)
  get(muteListChannelAtom)
  if (!pubkey) return { public: [], private: [] }

  const ml = await fetchMuteList(pubkey, true)
  return ml.list
})
export const unwrappedMuteListAtom = unwrap(muteListAtom)

export const mutePubkeySetAtom = atom((get) => {
  const m = get(unwrappedMuteListAtom)
  if (!m) return new Set<string>()
  return new Set([...m.private, ...m.public])
})

export const searchRelaysAtom = atom(async (get) => {
  const pubkey = get(currentPubkeyAtom)
  get(searchRelaysChannelAtom)
  if (!pubkey) return []

  const sr = await loadSearchRelays(pubkey)
  return sr.items
})
export const unwrappedSearchRelaysAtom = unwrap(searchRelaysAtom)

export const blossomServersAtom = atom(async (get) => {
  const pubkey = get(currentPubkeyAtom)
  get(blossomChannelAtom)
  if (!pubkey) return []

  const bs = await loadBlossomServers(pubkey)
  return bs.items
})
export const unwrappedBlossomServersAtom = unwrap(blossomServersAtom)

export const simpleGroupsAtom = atom(async (get) => {
  const pubkey = get(currentPubkeyAtom)
  get(simpleGroupsChannelAtom)
  if (!pubkey) return []

  const sg = await loadSimpleGroups(pubkey)
  return sg.items
})
export const unwrappedSimpleGroupsAtom = unwrap(simpleGroupsAtom)

export const blockedRelaysAtom = atom(async (get) => {
  const pubkey = get(currentPubkeyAtom)
  get(blockedRelaysChannelAtom)
  if (!pubkey) return []

  const br = await loadBlockedRelays(pubkey)
  return br.items
})
export const unwrappedBlockedRelaysAtom = unwrap(blockedRelaysAtom)

export const followListAtom = atom(async (get) => {
  const pubkey = get(currentPubkeyAtom)
  get(followListChannelAtom)
  if (!pubkey) return []

  const fl = await loadFollowsList(pubkey)
  return fl.items
})
export const unwrappedFollowListAtom = unwrap(followListAtom)

export const relayListAtom = atom(async (get) => {
  const pubkey = get(currentPubkeyAtom)
  get(relayListChannelAtom)
  if (!pubkey) return []

  const rl = await loadRelayList(pubkey)
  return rl.items
})
export const unwrappedRelayListAtom = unwrap(relayListAtom)

export const favoriteRelaysWithSetsAtom = atom(async (get) => {
  const pubkey = get(currentPubkeyAtom)
  get(favoritesChannelAtom)
  if (!pubkey) return []

  return await fetchFavoriteRelaysWithSets(pubkey)
})
export const unwrappedFavoriteRelaysWithSetsAtom = unwrap(favoriteRelaysWithSetsAtom)

export const favoriteFollowListsWithSetsAtom = atom(async (get) => {
  const pubkey = get(currentPubkeyAtom)
  get(favoriteFollowSetsChannelAtom)
  if (!pubkey) return []

  return await fetchFavoriteFollowSetsWithSets(pubkey)
})
export const unwrappedFavoriteFollowListsWithSetsAtom = unwrap(favoriteFollowListsWithSetsAtom)

export async function follow(targetPubkey: string) {
  const current = await store.get(followListAtom)
  if (current.includes(targetPubkey)) return

  const tags = [...current.map((pk) => ['p', pk] as string[]), ['p', targetPubkey]]
  const event = await publishOpaquely({
    kind: kinds.Contacts,
    tags,
    content: '',
    created_at: now()
  }).catch((err) => {
    console.warn('failed to publish updated follow list:', getErrorMessage(err))
    return null
  })
  if (!event) {
    toast.error(t('Failed to publish updated follow list.'))
    return
  }

  await loadFollowsList(event.pubkey, undefined, event)
  invalidateFollowList()
}

export async function unfollow(targetPubkey: string) {
  const current = await store.get(followListAtom)
  if (!current.includes(targetPubkey)) return

  const tags = current.filter((pk) => pk !== targetPubkey).map((pk) => ['p', pk] as string[])
  const event = await publishOpaquely({
    kind: kinds.Contacts,
    tags,
    content: '',
    created_at: now()
  }).catch((err) => {
    console.warn('failed to publish updated follow list:', getErrorMessage(err))
    return null
  })
  if (!event) {
    toast.error(t('Failed to publish updated follow list.'))
    return
  }

  await loadFollowsList(event.pubkey, undefined, event)
  invalidateFollowList()
}
